import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

export function CopyPgn({
  getPgn,
  disabled,
}: {
  getPgn: () => Promise<string>;
  disabled: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function copy(): Promise<void> {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const pgn = await getPgn();
      try {
        await navigator.clipboard.writeText(pgn);
        setCopied(true);
      } catch {
        setManual(pgn);
      }
    } catch {
      setError('Could not prepare PGN. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="copy-pgn"
        disabled={disabled || busy}
        aria-label="Copy PGN"
        title="Copy this line through the current position as PGN"
        onClick={() => void copy()}
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
        <span>{busy ? 'Preparing…' : copied ? 'Copied' : 'PGN'}</span>
      </button>
      <span className="sr-only" role="status">
        {copied ? 'PGN copied to clipboard.' : ''}
      </span>
      {error && (
        <span role="alert" className="error">
          {error}
        </span>
      )}
      <Dialog
        open={manual !== null}
        onOpenChange={(open) => {
          if (!open) setManual(null);
        }}
      >
        <DialogContent>
          <DialogTitle>Copy PGN</DialogTitle>
          <DialogDescription>
            Clipboard access is unavailable. Select and copy the PGN below.
          </DialogDescription>
          <textarea
            className="pgn-text"
            aria-label="PGN"
            readOnly
            value={manual ?? ''}
            onFocus={(e) => e.target.select()}
            rows={10}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
